<!-- src/views/PlanPreludePage.vue -->
<template>
  <ion-page>
    <ion-content
      :fullscreen="true"
      class="p-[10%] sm:p-[8%] md:p-[6%] lg:p-[5%]"
    >
      <!-- Floating Home Button -->
      <ion-fab slot="fixed" class="md:mr-[5%] lg:mt-[4%] md:mt-[6%] mt-[1%]">
        <ion-fab-button @click="goHome">
          <ion-icon :icon="homeSharp" />
        </ion-fab-button>
      </ion-fab>

      <!-- Slide Header -->
      <b-slide-header />

      <!-- Main Content -->
      <div
        class="flex justify-center flex-col-reverse md:flex-row items-center relative md:w-[80%] mx-auto"
      >
        <img class="monster md:mt-0" :src="monsterUrl" />

        <div class="relative flex justify-center items-center">
          <speech-bubble
            :color="'yellow'"
            class="w-[100%] md:rotate-[70deg] -scale-x-100 -z-5"
          />

          <div
            class="absolute md:pl-5 lg:text-[15px] text-[min(5vw,1.15rem)] text-center w-[80%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            Inte långt kvar nu. Nu är det dags att bestämma VAD du ska göra för
            att nå dina MÅL.
          </div>
        </div>
      </div>
    </ion-content>

    <!-- Next Button -->
    <div
      class="lg:bg-[#fff3d2] fixed bottom-0 w-full px-8 lg:h-[14%] pt-10 pb-6 button-border"
      ref="navigationArea"
    >
      <div
        class="grid grid-cols-1 gap-4 lg:grid-cols-[2.7fr_0.6fr_0fr] lg:mt-[-10%]"
      >
        <div class="hidden lg:block"></div>
        <b-button @click="next" class="w-full lg:w-full">{{ t('navigation.next') }}</b-button>
        <div class="hidden lg:block"></div>
      </div>
    </div>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonPage,
  IonIcon,
  useIonRouter,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import Globe from '@/components/illustrations/Globe.vue';
import { provide } from 'vue';
import { homeSharp } from 'ionicons/icons';
import { useStore } from '@/stores/main';
import monsterUrl from '/assets/monster/glad.png';
import SpeechBubble from '@/components/SpeechBubble.vue';
import { useI18n } from 'vue-i18n';
import BButton from '@/components/BButton.vue';

const { t } = useI18n();

const store = useStore();
const router = useIonRouter();

const goHome = () => router.navigate('/evaluate', 'root', 'replace');

async function next() {
  try {
    // Get the first goal's ID and navigate directly to its actions page
    let firstGoalId: number | null = null;

    for (let attempt = 0; attempt < 3 && !firstGoalId; attempt++) {
      const planned = await store.fetchPlannedGoals();
      if (planned && planned.length > 0) {
        firstGoalId = planned[0].category_id;
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    if (!firstGoalId) {
      const goals = await store.fetchGoals();
      const improving = (goals || []).filter((g: any) => Number(g.selected) === 1 && Number(g.vision) > Number(g.value));
      if (improving.length > 0) {
        firstGoalId = improving[0].category_id;
      }
    }

    if (firstGoalId) {
      console.log(`🎯 Navigating to /plan/${firstGoalId}/actions`);
      // Use window.location.href for reliable navigation
      window.location.href = `/plan/${firstGoalId}/actions`;
    } else {
      console.error('No goals found after update');
      window.location.href = '/goals/improve';
    }
  } catch (error) {
    console.error('Failed to fetch goals:', error);
    window.location.href = '/goals/improve';
  }
}

provide('pageData', {
  number: 4,
  title: 'Min livsstilsplan',
  icon: Globe,
});
provide('colorScheme', 'yellow');
</script>

<style scoped>
ion-fab {
  right: 0.5rem;
  top: 90px;
}
ion-content {
  --keyboard-offset: 0 !important;
}
.monster {
  transition: transform 0.3s ease-in-out;
}
</style>